import type { CharacterConfig, CharacterPerks } from '../config/character.config';
import { clamp } from '../utils/math';
import type { CharacterCatalog } from './CharacterCatalog';
import type { CharacterArt } from './CharacterTypes';

/**
 * Pure rules of the character selection (no DOM, no Babylon): which characters are offered,
 * what their cards say, which perks a run gets and how a stored or requested choice is resolved.
 */

/** Perks of a character without an entry (greybox placeholder): plays exactly like the base config. */
export const NEUTRAL_PERKS: CharacterPerks = {
  title: '',
  advantage: '',
  disadvantage: '',
  startExtraLives: 0,
  bottlesPerExtraLifeFactor: 1,
  magnetRadius: 0,
  neighbourLaneBottleLimit: 0,
  stumbleCostsExtraLife: false,
};

export function perksOf(characterId: string, config: CharacterConfig): CharacterPerks {
  return config.perks[characterId] ?? NEUTRAL_PERKS;
}

/** Content of one selection card. */
export interface CharacterChoice {
  readonly id: string;
  readonly name: string;
  readonly motto: string;
  readonly perkTitle: string;
  readonly advantage: string;
  readonly disadvantage: string;
  /** Portrait / figure pictures of the menus (null: text-only card). */
  readonly art: CharacterArt | null;
}

/** Cards for all selectable characters, in the configured order. */
export function characterChoices(catalog: CharacterCatalog, config: CharacterConfig): CharacterChoice[] {
  return config.selectable.map((id) => {
    const character = catalog.get(id);
    const perks = perksOf(id, config);
    return {
      id,
      name: character.displayName,
      motto: character.motto,
      perkTitle: perks.title,
      advantage: perks.advantage,
      disadvantage: perks.disadvantage,
      art: character.art,
    };
  });
}

/** A stored or requested id if it is (still) selectable – otherwise the default character. */
export function resolveCharacterId(requested: string | null | undefined, config: CharacterConfig): string {
  return requested && config.selectable.includes(requested) ? requested : config.selected;
}

/** Left/right on the selection: the neighbouring card, clamped at the ends (left arrow = left card). */
export function stepCharacter(current: string, direction: -1 | 1, config: CharacterConfig): string {
  const index = config.selectable.indexOf(current);
  if (index < 0) return resolveCharacterId(null, config);
  return config.selectable[clamp(index + direction, 0, config.selectable.length - 1)] ?? current;
}

/**
 * ?character= testing aid: a selectable character's id, one of its id words ("electrician",
 * "carpenter") or its display name ("Zimmerer"), case-insensitive. Null when nothing matches.
 */
export function findCharacterId(query: string | null, catalog: CharacterCatalog, config: CharacterConfig): string | null {
  const needle = query?.trim().toUpperCase();
  if (!needle) return null;
  return (
    config.selectable.find(
      (id) => id === needle || id.split('_').includes(needle) || catalog.get(id).displayName.toUpperCase() === needle,
    ) ?? null
  );
}
